import { Component, Input, OnInit } from '@angular/core';
import { Block, BlockAbstractComponent } from '@gallaghercp/core-frontend';
import { BlockTimelineChartComponent } from '@core-app/blocks/block-leave-of-absence-tool/results/timeline-chart/block-timeline-chart.component';
import { TranslatePipe } from '@gallaghercp/core-frontend-contracts';

@Component({
    selector: 'core-giving-birth-results',
    templateUrl: './block-giving-birth-results.component.html',
    styleUrls: [
        '../../scss/block-leave-of-absence-tool-charts.scss',
        '../../scss/block-leave-of-absence-tool-questions.scss',
        '../../scss/block-leave-of-absence-tool-timeline.scss',
    ],
    imports: [BlockTimelineChartComponent, TranslatePipe],
})
export class BlockGivingBirthResultsComponent extends BlockAbstractComponent implements OnInit {
    @Input() selectedAnswers: Record<string, any> = {};
    @Input() birthTimelineChartKey: string = '';
    @Input() parentBlock: Block | undefined;

    // Section text strings
    introParagraphText!: string;
    lengthOfLeaveText!: string;
    payText!: string;
    processChecklist!: string;
    thingsToConsider!: string;

    override ngOnInit() {
        // Create a mapping of conditions to their corresponding intro paragraph text
        const introParagraphMapping = [
            {
                condition: !this.birthNinetyDaysOrLess,
                text: this.parentBlock?.data.givingBirthIntroParagraphOverNinetyDaysText,
            },
            {
                condition: this.birthNinetyDaysOrLess,
                text: this.parentBlock?.data.givingBirthIntroParagraphNinetyDaysOrLessText,
            },
        ];

        // Create a mapping of conditions to their corresponding length of leave text
        const lengthOfLeaveMapping = [
            {
                condition: this.birthTwelveMonthsOrMoreHourlyNatural,
                text: this.parentBlock?.data.givingBirthLengthOfLeaveTwelveWeeksJobProtected,
            },
            {
                condition: this.birthTwelveMonthsOrMoreHourlyCesarean,
                text: this.parentBlock?.data.givingBirthLengthOfLeaveFourteenWeeksJobProtected,
            },
            {
                condition: this.birthTwelveMonthsOrMoreSalariedNatural,
                text: this.parentBlock?.data.givingBirthLengthOfLeaveTwelveWeeksJobProtected,
            },
            {
                condition: this.birthTwelveMonthsOrMoreSalariedCesarean,
                text: this.parentBlock?.data.givingBirthLengthOfLeaveFourteenWeeksJobProtected,
            },
            {
                condition: this.birthLessThanTwelveMonthsButMoreThanNinetyDaysHourlyNatural,
                text: this.parentBlock?.data.givingBirthLengthOfLeaveTwelveWeeksJobNotProtected,
            },
            {
                condition: this.birthLessThanTwelveMonthsButMoreThanNinetyDaysHourlyCesarean,
                text: this.parentBlock?.data.givingBirthLengthOfLeaveFourteenWeeksJobNotProtected,
            },
            {
                condition: this.birthLessThanTwelveMonthsButMoreThanNinetyDaysSalariedNatural,
                text: this.parentBlock?.data.givingBirthLengthOfLeaveTwelveWeeksJobNotProtected,
            },
            {
                condition: this.birthLessThanTwelveMonthsButMoreThanNinetyDaysSalariedCesarean,
                text: this.parentBlock?.data.givingBirthLengthOfLeaveFourteenWeeksJobNotProtected,
            },
        ];

        // Create a mapping of conditions to their corresponding pay text
        const payMapping = [
            {
                condition: this.birthTwelveMonthsOrMoreHourlyNatural,
                text: this.parentBlock?.data.givingBirthPaySixWeeksAtSixtyPercent,
            },
            {
                condition: this.birthTwelveMonthsOrMoreHourlyCesarean,
                text: this.parentBlock?.data.givingBirthPayEightWeeksAtSixtyPercent,
            },
            {
                condition: this.birthTwelveMonthsOrMoreSalariedNatural,
                text: this.parentBlock?.data.givingBirthPaySixWeeksAtOneHundredPercent,
            },
            {
                condition: this.birthTwelveMonthsOrMoreSalariedCesarean,
                text: this.parentBlock?.data.givingBirthPayEightWeeksAtOneHundredPercent,
            },
            {
                condition: this.birthLessThanTwelveMonthsButMoreThanNinetyDaysHourlyNatural,
                text: this.parentBlock?.data.givingBirthPaySixWeeksAtSixtyPercent,
            },
            {
                condition: this.birthLessThanTwelveMonthsButMoreThanNinetyDaysHourlyCesarean,
                text: this.parentBlock?.data.givingBirthPayEightWeeksAtSixtyPercent,
            },
            {
                condition: this.birthLessThanTwelveMonthsButMoreThanNinetyDaysSalariedNatural,
                text: this.parentBlock?.data.givingBirthPaySixWeeksAtOneHundredPercent,
            },
            {
                condition: this.birthLessThanTwelveMonthsButMoreThanNinetyDaysSalariedCesarean,
                text: this.parentBlock?.data.givingBirthPayEightWeeksAtOneHundredPercent,
            },
        ];

        // Create a mapping of conditions to their corresponding process list text
        const processListMapping = [
            {
                condition: !this.birthNinetyDaysOrLess,
                text: this.parentBlock?.data.givingBirthProcessList,
            },
        ];

        // Create a mapping of conditions to their corresponding things to consider text
        const thingsToConsiderMapping = [
            {
                condition: !this.birthNinetyDaysOrLess,
                text: this.parentBlock?.data.givingBirthThingsToConsiderOverNinetyDays,
            },
            {
                condition: this.birthNinetyDaysOrLess,
                text: this.parentBlock?.data.givingBirthThingsToConsiderNinetyDaysOrLess,
            },
        ];

        // Find the first matching condition and set the intro paragraph text
        const introParagraphMatch = introParagraphMapping.find(item => item.condition);
        if (introParagraphMatch) {
            this.introParagraphText = introParagraphMatch.text;
        }

        // Find the first matching condition and set the length of leave text
        const lengthOfLeaveMatch = lengthOfLeaveMapping.find(item => item.condition);
        if (lengthOfLeaveMatch) {
            this.lengthOfLeaveText = lengthOfLeaveMatch.text;
        }

        // Find the first matching condition and set the pay text
        const payMatch = payMapping.find(item => item.condition);
        if (payMatch) {
            this.payText = payMatch.text;
        }

        // Find the first matching condition and set the process list text
        const processListMatch = processListMapping.find(item => item.condition);
        if (processListMatch) {
            this.processChecklist = processListMatch.text;
        }

        // Find the first matching condition and set the things to consider text
        const thingsToConsiderMatch = thingsToConsiderMapping.find(item => item.condition);
        if (thingsToConsiderMatch) {
            this.thingsToConsider = thingsToConsiderMatch.text;
        }
        super.ngOnInit();
    }

    get birthTwelveMonthsOrMoreHourlyNatural(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'twelveMonthsOrMore' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'hourly' &&
            this.selectedAnswers['deliveryType'] == 'natural'
        );
    }

    get birthTwelveMonthsOrMoreHourlyCesarean(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'twelveMonthsOrMore' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'hourly' &&
            this.selectedAnswers['deliveryType'] == 'cesarean'
        );
    }

    get birthTwelveMonthsOrMoreSalariedNatural(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'twelveMonthsOrMore' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'salaried' &&
            this.selectedAnswers['deliveryType'] == 'natural'
        );
    }

    get birthTwelveMonthsOrMoreSalariedCesarean(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'twelveMonthsOrMore' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'salaried' &&
            this.selectedAnswers['deliveryType'] == 'cesarean'
        );
    }

    get birthLessThanTwelveMonthsButMoreThanNinetyDaysHourlyNatural(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'lessThanTwelveMonthsButMoreThanNinetyDays' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'hourly' &&
            this.selectedAnswers['deliveryType'] == 'natural'
        );
    }

    get birthLessThanTwelveMonthsButMoreThanNinetyDaysHourlyCesarean(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'lessThanTwelveMonthsButMoreThanNinetyDays' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'hourly' &&
            this.selectedAnswers['deliveryType'] == 'cesarean'
        );
    }

    get birthLessThanTwelveMonthsButMoreThanNinetyDaysSalariedNatural(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'lessThanTwelveMonthsButMoreThanNinetyDays' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'salaried' &&
            this.selectedAnswers['deliveryType'] == 'natural'
        );
    }

    get birthLessThanTwelveMonthsButMoreThanNinetyDaysSalariedCesarean(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'lessThanTwelveMonthsButMoreThanNinetyDays' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'salaried' &&
            this.selectedAnswers['deliveryType'] == 'cesarean'
        );
    }

    get birthNinetyDaysOrLess(): boolean {
        return this.selectedAnswers['employedDuration'] == 'ninetyDaysOrLess';
    }
}
